import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaChevronLeft, FaChevronRight, FaSyncAlt } from "react-icons/fa";
import { downloadCSV, downloadPDF, printThermal } from "../utils/exportHelpers";

const COLOR = {
  primary: "#015b9a",
  bg: "#f5f7fb",
  card: "#ffffff",
  line: "#e5e7eb",
  text: "#0f172a",
  muted: "#6b7280",
};

const REPORT_MODE_DAY = "day";
const REPORT_MODE_MONTH = "month";
const DAY_ENDPOINT = "https://qr.billsarthi.in/public/api/report-v2/day-wise-items";
const MONTH_ENDPOINT =
  "https://qr.billsarthi.in/public/api/report-v2/month-wise-items";

const buildDefaultReport = ({ mode, date, month }) => ({
  mode,
  date,
  month,
  restaurant: "",
  total_items: 0,
  total_quantity: 0,
  total_amount: 0,
  total_orders: 0,
  items: [],
});

const aggregateCategories = (items = []) => {
  const map = new Map();

  items.forEach((item) => {
    const key = String(item?.category_name || "").trim() || "Uncategorized";
    const current = map.get(key) || {
      category_name: key,
      item_count: 0,
      total_quantity: 0,
      total_orders: 0,
      total_amount: 0,
    };

    current.item_count += 1;
    current.total_quantity += Number(item?.total_quantity || 0);
    current.total_orders += Number(item?.total_orders || 0);
    current.total_amount += Number(item?.total_amount || 0);

    map.set(key, current);
  });

  return Array.from(map.values()).sort((a, b) => {
    if (b.total_quantity !== a.total_quantity) {
      return b.total_quantity - a.total_quantity;
    }
    return b.total_amount - a.total_amount;
  });
};

const DayWiseCategoryReportPage = () => {
  const [userId, setUserId] = useState(null);
  const [mode, setMode] = useState(REPORT_MODE_DAY);
  const [date, setDate] = useState(today());
  const [month, setMonth] = useState(todayMonth());
  const [report, setReport] = useState(
    buildDefaultReport({ mode: REPORT_MODE_DAY, date: today(), month: todayMonth() })
  );
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;
  const reportRef = useRef(null);

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("userData") || "{}")?.data;
    if (!user?.id) {
      toast.error("User not found. Please login again.");
      setLoading(false);
      return;
    }
    setUserId(user.id);
  }, []);

  useEffect(() => {
    if (!userId) return;
    fetchReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, mode, date, month]);

  const fetchReport = async (opts = {}) => {
    if (!userId) return;

    const activeDate = date;
    const activeMonth = month;
    const endpoint = mode === REPORT_MODE_MONTH ? MONTH_ENDPOINT : DAY_ENDPOINT;
    const body =
      mode === REPORT_MODE_MONTH
        ? { restaurantid: userId, month: activeMonth }
        : { restaurantid: userId, date: activeDate };

    opts.refresh ? setRefreshing(true) : setLoading(true);

    try {
      const res = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok || !json?.success) {
        throw new Error(json?.message || "Failed to load report");
      }

      const items = aggregateCategories(Array.isArray(json?.items) ? json.items : []);
      setReport({
        mode,
        date: json?.date || activeDate,
        month: json?.month || activeMonth,
        restaurant: json?.restaurant || "",
        total_items: items.length,
        total_quantity: Number(
          json?.total_quantity ??
            items.reduce((sum, item) => sum + Number(item.total_quantity || 0), 0)
        ),
        total_amount: Number(
          json?.total_amount ??
            items.reduce((sum, item) => sum + Number(item.total_amount || 0), 0)
        ),
        total_orders: Number(
          json?.total_orders ??
            items.reduce((sum, item) => sum + Number(item.total_orders || 0), 0)
        ),
        items,
      });
      setPage(0);
    } catch (error) {
      toast.error(error.message || "Unable to load report");
      setReport(buildDefaultReport({ mode, date: activeDate, month: activeMonth }));
    } finally {
      opts.refresh ? setRefreshing(false) : setLoading(false);
    }
  };

  const changeSelection = (delta) => {
    if (mode === REPORT_MODE_MONTH) {
      const [year, currentMonth] = month.split("-").map(Number);
      const next = new Date(year, (currentMonth || 1) - 1 + delta, 1);
      setMonth(formatMonth(next));
      return;
    }

    const next = new Date(date);
    next.setDate(next.getDate() + delta);
    setDate(toISO(next));
  };

  const paginated = useMemo(() => {
    const start = page * rowsPerPage;
    return {
      start,
      rows: report.items.slice(start, start + rowsPerPage),
    };
  }, [page, report.items]);

  const pageCount = Math.ceil((report.items.length || 0) / rowsPerPage);

  const fmtINR = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    })}`;

  const exportCsv = () => {
    const rows = report.items.map((item, index) => [
      index + 1,
      item.category_name,
      item.item_count,
      item.total_quantity,
      item.total_orders,
      item.total_amount,
    ]);

    downloadCSV(
      `day_wise_category_${mode === REPORT_MODE_MONTH ? month : date}.csv`,
      ["#", "Category", "Items", "Qty", "Orders", "Amount"],
      rows
    );
  };

  return (
    <Page>
      <ToastContainer position="top-right" autoClose={2200} />
      <Top>
        <div>
          <Title>Day-wise Category Report</Title>
          <Sub>Category totals grouped from item sales, with day and month views.</Sub>
          <PillRow>
            <Pill>Categories: {report.total_items || 0}</Pill>
            <Pill>Total Qty: {report.total_quantity || 0}</Pill>
            <Pill>Total Orders: {report.total_orders || 0}</Pill>
            <Pill>Total Amount: {fmtINR(report.total_amount)}</Pill>
          </PillRow>
        </div>
        <Actions>
          <ModeWrap>
            <ModeBtn
              type="button"
              $active={mode === REPORT_MODE_DAY}
              onClick={() => setMode(REPORT_MODE_DAY)}
            >
              Day
            </ModeBtn>
            <ModeBtn
              type="button"
              $active={mode === REPORT_MODE_MONTH}
              onClick={() => setMode(REPORT_MODE_MONTH)}
            >
              Month
            </ModeBtn>
          </ModeWrap>
          <NavBtn type="button" onClick={() => changeSelection(-1)}>
            <FaChevronLeft />
          </NavBtn>
          <DateInput>
            {mode === REPORT_MODE_MONTH ? "Month:" : "Date:"}
            {mode === REPORT_MODE_MONTH ? (
              <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
            ) : (
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} />
            )}
          </DateInput>
          <NavBtn type="button" onClick={() => changeSelection(1)}>
            <FaChevronRight />
          </NavBtn>
          <PrimaryBtn type="button" onClick={() => fetchReport({ refresh: true })}>
            <FaSyncAlt /> {refreshing ? "Refreshing..." : "Refresh"}
          </PrimaryBtn>
        </Actions>
      </Top>

      <Card ref={reportRef}>
        {loading ? (
          <Center>Loading…</Center>
        ) : report.items.length === 0 ? (
          <Center>No data found for selected {mode}.</Center>
        ) : (
          <TableWrap>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Category</th>
                  <th>Items</th>
                  <th>Qty</th>
                  <th>Orders</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {paginated.rows.map((item, index) => (
                  <tr key={`${item.category_name}-${index}`}>
                    <td>{paginated.start + index + 1}</td>
                    <td>{item.category_name}</td>
                    <td>{item.item_count}</td>
                    <td>{item.total_quantity}</td>
                    <td>{item.total_orders}</td>
                    <td>{fmtINR(item.total_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}

        <Pager>
          <PageBtn type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
            ‹ Prev
          </PageBtn>
          <PageInfo>
            Page <strong>{pageCount ? page + 1 : 0}</strong> of <strong>{pageCount}</strong>
          </PageInfo>
          <PageBtn
            type="button"
            disabled={page + 1 >= pageCount}
            onClick={() => setPage((p) => p + 1)}
          >
            Next ›
          </PageBtn>
        </Pager>

        <Actions style={{ marginTop: 10 }}>
          <PrimaryBtn
            type="button"
            onClick={() =>
              downloadPDF(
                reportRef.current,
                `day_wise_category_${mode === REPORT_MODE_MONTH ? month : date}.pdf`
              )
            }
          >
            PDF
          </PrimaryBtn>
          <PrimaryBtn
            type="button"
            onClick={() =>
              printThermal(
                reportRef.current,
                `day_wise_category_${mode === REPORT_MODE_MONTH ? month : date}`
              )
            }
          >
            Thermal Print
          </PrimaryBtn>
          <PrimaryBtn type="button" onClick={exportCsv}>
            CSV
          </PrimaryBtn>
        </Actions>
      </Card>
    </Page>
  );
};

export default DayWiseCategoryReportPage;

const today = () => toISO(new Date());
const todayMonth = () => formatMonth(new Date());
const toISO = (date) => date.toISOString().slice(0, 10);
const formatMonth = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const Page = styled.div`
  background: ${COLOR.bg};
  min-height: 100vh;
  padding: 16px;
`;

const Top = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 12px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 900;
  color: ${COLOR.text};
`;

const Sub = styled.div`
  color: ${COLOR.muted};
`;

const PillRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
  flex-wrap: wrap;
`;

const Pill = styled.span`
  background: #eef2ff;
  color: ${COLOR.text};
  padding: 8px 10px;
  border-radius: 12px;
  font-weight: 800;
  border: 1px solid ${COLOR.line};
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const ModeWrap = styled.div`
  display: inline-flex;
  background: #e2e8f0;
  border-radius: 12px;
  padding: 4px;
  gap: 4px;
`;

const ModeBtn = styled.button`
  border: 0;
  border-radius: 10px;
  padding: 8px 12px;
  font-weight: 800;
  cursor: pointer;
  background: ${(props) => (props.$active ? COLOR.primary : "transparent")};
  color: ${(props) => (props.$active ? "#fff" : COLOR.text)};
`;

const DateInput = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 10px;
  border: 1px solid ${COLOR.line};
  border-radius: 10px;
  background: #f9f9f9;
  color: ${COLOR.text};

  input {
    border: 0;
    background: transparent;
    outline: none;
  }
`;

const NavBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: #fff;
  color: ${COLOR.text};
  border-radius: 10px;
  padding: 10px 12px;
  cursor: pointer;
`;

const PrimaryBtn = styled.button`
  background: ${COLOR.primary};
  color: #fff;
  border: 0;
  border-radius: 10px;
  padding: 10px 12px;
  font-weight: 800;
  cursor: pointer;
  display: inline-flex;
  gap: 8px;
  align-items: center;
`;

const Card = styled.div`
  background: ${COLOR.card};
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
  padding: 12px;
`;

const TableWrap = styled.div`
  overflow: auto;
  border: 1px solid ${COLOR.line};
  border-radius: 14px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 720px;

  th,
  td {
    border-bottom: 1px solid ${COLOR.line};
    padding: 12px;
    text-align: left;
    font-size: 0.95rem;
  }

  th {
    background: #eff6ff;
    color: ${COLOR.text};
    font-weight: 800;
  }
`;

const Center = styled.div`
  text-align: center;
  color: ${COLOR.muted};
  padding: 32px 16px;
`;

const Pager = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 12px;
  flex-wrap: wrap;
`;

const PageBtn = styled.button`
  border: 1px solid ${COLOR.line};
  background: #fff;
  color: ${COLOR.text};
  border-radius: 10px;
  padding: 8px 12px;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const PageInfo = styled.div`
  color: ${COLOR.muted};
`;
